import { z } from 'zod'

/**
 * Brought-forward balances (ยกยอด) when the company starts mid-year from other programs and
 * spreadsheets: what each employee was paid, withheld and contributed this year, the leave they
 * already took, the invoices customers still owe and the bills still to pay. Each comes from an
 * Excel sheet in a fixed layout (`openingColumns`): every row is checked first, and nothing is
 * written until all of them are right. Brought-forward invoices and bills keep their old numbers
 * and never reach the input VAT report or the journal: the old books have them.
 */

export const openingKinds = ['payroll', 'leave', 'receivables', 'payables'] as const
export type OpeningKind = (typeof openingKinds)[number]

const code = z.string().trim().min(1).max(30)
const money = z.number().min(0).max(100_000_000)

export const openingRows = {
  /** One employee's year so far, before their first payslip here */
  payroll: z.object({ employeeCode: code, income: money, tax: money, sso: z.number().min(0).max(1_000_000) }),
  /** Days of one kind of leave one employee took this year before the system */
  leave: z.object({ employeeCode: code, leaveTypeCode: code, days: z.number().min(0).max(366) }),
  /** An invoice a customer still owes, as the old program numbered it */
  receivables: z.object({
    customerCode: code,
    number: code,
    issueDate: z.iso.date(),
    dueDate: z.iso.date().nullish(),
    /** What is still owed, VAT in */
    amount: z.number().positive().max(100_000_000),
    notes: z.string().trim().max(300).nullish(),
  }),
  /** A supplier's bill still to pay */
  payables: z.object({
    vendorCode: code,
    number: code,
    billDate: z.iso.date(),
    dueDate: z.iso.date().nullish(),
    /** What is still to pay, VAT in, before withholding */
    amount: z.number().positive().max(100_000_000),
    /** Withholding tax to take when paying it */
    wht: z.number().min(0).max(100_000_000).default(0),
    notes: z.string().trim().max(300).nullish(),
  }),
} as const

export type OpeningRow<K extends OpeningKind> = z.infer<(typeof openingRows)[K]>

/** Columns of each sheet, in order: the field and how a cell is read */
export const openingColumns: Record<OpeningKind, { key: string; type: 'text' | 'number' | 'date' }[]> = {
  payroll: [
    { key: 'employeeCode', type: 'text' },
    { key: 'income', type: 'number' },
    { key: 'tax', type: 'number' },
    { key: 'sso', type: 'number' },
  ],
  leave: [
    { key: 'employeeCode', type: 'text' },
    { key: 'leaveTypeCode', type: 'text' },
    { key: 'days', type: 'number' },
  ],
  receivables: [
    { key: 'customerCode', type: 'text' },
    { key: 'number', type: 'text' },
    { key: 'issueDate', type: 'date' },
    { key: 'dueDate', type: 'date' },
    { key: 'amount', type: 'number' },
    { key: 'notes', type: 'text' },
  ],
  payables: [
    { key: 'vendorCode', type: 'text' },
    { key: 'number', type: 'text' },
    { key: 'billDate', type: 'date' },
    { key: 'dueDate', type: 'date' },
    { key: 'amount', type: 'number' },
    { key: 'wht', type: 'number' },
    { key: 'notes', type: 'text' },
  ],
}

/** A cell as the sheet gave it, as a value: numbers without commas, dates as ISO (a Buddhist-era year back to the common one) */
export function openingCell(value: unknown, type: 'text' | 'number' | 'date'): unknown {
  if (value == null || value === '') return null
  if (type === 'number') {
    const n = typeof value === 'number' ? value : Number(String(value).replace(/[,\s฿]/g, ''))
    return Number.isFinite(n) ? n : value
  }
  if (type === 'date') {
    if (value instanceof Date) return value.toISOString().slice(0, 10)
    const text = String(value).trim()
    const dmy = text.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/)
    if (dmy) {
      const year = Number(dmy[3]) > 2400 ? Number(dmy[3]) - 543 : Number(dmy[3])
      return `${year}-${dmy[2]?.padStart(2, '0')}-${dmy[1]?.padStart(2, '0')}`
    }
    const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (iso) return `${Number(iso[1]) > 2400 ? Number(iso[1]) - 543 : iso[1]}-${iso[2]}-${iso[3]}`
    return text
  }
  return String(value).trim()
}

/** A sheet's rows as objects by the kind's columns, in order (the header row left out) */
export function openingRecords(kind: OpeningKind, rows: readonly unknown[][]): Record<string, unknown>[] {
  const columns = openingColumns[kind]
  return rows
    .filter((row) => row.some((cell) => cell != null && String(cell).trim() !== ''))
    .map((row) => Object.fromEntries(columns.map((c, i) => [c.key, openingCell(row[i], c.type)])))
}
